<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
    <v-alert v-if="error" type="error" outlined dense>{{ error }}</v-alert>

    <!-- ===================== มุมมองหน่วยงาน ===================== -->
    <template v-if="mode === 'unit' && unit">
      <div class="d-flex flex-wrap align-center mb-3" style="gap:8px">
        <span class="subtitle-1 font-weight-bold">
          <v-icon>mdi-school</v-icon> {{ unit.unit.college_name }}
        </span>
        <v-chip small outlined>ปีงบประมาณ {{ unit.fiscal_year }}</v-chip>
        <v-chip small outlined>ข้อมูล ณ {{ unit.today }}</v-chip>
        <v-spacer />
        <v-chip small :color="overall.color" dark>
          <v-icon small left>{{ overall.icon }}</v-icon>{{ overall.text }}
        </v-chip>
      </div>

      <v-row dense>
        <v-col v-for="(c, i) in cards" :key="c.key" cols="12" md="6" lg="4">
          <v-card outlined class="step-card" :class="'step-' + c.level">
            <v-card-title class="py-2 subtitle-2 font-weight-bold">
              <v-avatar size="24" :color="levelColor(c.level)" class="mr-2">
                <span class="white--text caption">{{ i + 1 }}</span>
              </v-avatar>
              {{ c.title }}
              <v-spacer />
              <v-icon :color="levelColor(c.level)">{{ levelIcon(c.level) }}</v-icon>
            </v-card-title>
            <v-card-text class="pb-2">
              <div v-for="(l, j) in c.lines" :key="j" class="caption" :class="l.cls">{{ l.text }}</div>
              <div v-if="c.list && c.list.length" class="mt-1">
                <v-chip
                  v-for="p in c.list.slice(0, 8)"
                  :key="c.key + p.id_card"
                  x-small
                  outlined
                  :color="levelColor(c.level)"
                  class="mr-1 mb-1"
                >
                  {{ p.full_name }} · {{ p.detail }}
                </v-chip>
                <span v-if="c.list.length > 8" class="caption grey--text">และอีก {{ c.list.length - 8 }} คน</span>
              </div>
            </v-card-text>
            <v-card-actions v-if="c.to" class="pt-0">
              <v-spacer />
              <v-btn x-small text color="primary" :to="c.to">
                {{ c.action || "ไปที่หน้าดำเนินการ" }} <v-icon x-small right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- ===================== มุมมองส่วนกลาง ===================== -->
    <template v-if="mode === 'national' && national">
      <v-row dense class="mb-2">
        <v-col v-for="k in nationalKpis" :key="k.label" cols="6" md="2">
          <v-card outlined class="pa-2 text-center" :class="k.value && k.bad ? k.bg : ''">
            <div class="title" :class="k.value && k.bad ? k.color : 'grey--text text--darken-2'">{{ fmt(k.value) }}</div>
            <div class="caption">{{ k.label }}</div>
            <div class="caption grey--text">{{ k.hint }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense align="center" class="mb-1">
        <v-col cols="12" md="4">
          <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify"
            label="ค้นหาหน่วยงาน / จังหวัด / รหัส" />
        </v-col>
        <v-col cols="12" md="6">
          <v-btn-toggle v-model="filter" dense mandatory>
            <v-btn x-small value="all">ทั้งหมด</v-btn>
            <v-btn x-small value="action">มีเรื่องต้องดำเนินการ ({{ actionUnits.length }})</v-btn>
            <v-btn x-small value="ok">เรียบร้อย</v-btn>
          </v-btn-toggle>
        </v-col>
        <v-col cols="12" md="2" class="text-right">
          <v-btn small color="success" @click="exportXlsx">
            <v-icon small left>mdi-microsoft-excel</v-icon>xlsx
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :headers="nationalHeaders"
        :items="filteredUnits"
        :search="search"
        dense
        item-key="college_code"
        :footer-props="{ itemsPerPageOptions: [20, 50, 100, -1] }"
        :items-per-page="20"
        class="hub-table"
      >
        <template v-slot:item.college_name="{ item }">
          <a class="font-weight-medium" @click="openUnit(item)">{{ item.college_name }}</a>
          <v-chip v-if="item.unit_type === 'C'" x-small outlined color="purple" class="ml-1">สถาบัน</v-chip>
          <v-chip v-if="item.unit_type === 'V'" x-small outlined color="teal" class="ml-1">ส่วนกลาง</v-chip>
          <div class="caption grey--text">{{ item.college_code }} · {{ item.province_name || "-" }}</div>
        </template>
        <template v-slot:item.frame_filled="{ item }">
          {{ item.frame_filled }}/{{ item.frame_total }}
          <div class="caption" :class="item.registry !== item.frame_filled ? 'warning--text' : 'grey--text'">
            ทะเบียน {{ item.registry }}
          </div>
        </template>
        <template v-slot:item.contract_expired="{ item }">
          <span v-if="item.contract_expired" class="error--text font-weight-bold">หมด {{ item.contract_expired }}</span>
          <span v-if="item.contract_expiring" class="warning--text"> ใกล้หมด {{ item.contract_expiring }}</span>
          <div v-if="item.contract_none" class="caption grey--text">ไม่มีข้อมูล {{ item.contract_none }}</div>
        </template>
        <template v-slot:item.resign_overdue="{ item }">
          <span v-if="item.resign_overdue" class="error--text font-weight-bold">{{ item.resign_overdue }}</span>
          <span v-else class="grey--text">-</span>
          <span v-if="item.resign_upcoming" class="caption grey--text"> (รอมีผล {{ item.resign_upcoming }})</span>
        </template>
        <template v-slot:item.prev_missing="{ item }">
          <span :class="item.prev_missing ? 'error--text font-weight-bold' : 'success--text'">
            {{ item.prev_required - item.prev_missing }}/{{ item.prev_required }}
          </span>
          <div v-if="item.prev_unapproved" class="caption warning--text">ยังไม่อนุมัติ {{ item.prev_unapproved }}</div>
        </template>
        <template v-slot:item.attendance_missing="{ item }">
          <span :class="item.attendance_missing ? 'warning--text' : 'grey--text'">{{ item.attendance_missing || "-" }}</span>
        </template>
        <template v-slot:item.cert_status="{ item }">
          <v-chip x-small dark :color="certColor(item.cert_status)">{{ certText(item.cert_status) }}</v-chip>
        </template>
        <template v-slot:item.transfer_incoming="{ item }">
          <span :class="item.transfer_incoming ? 'warning--text font-weight-bold' : 'grey--text'">
            {{ item.transfer_incoming || "-" }}
          </span>
        </template>
      </v-data-table>

      <v-dialog v-model="unitDialog" max-width="1200" scrollable>
        <v-card v-if="unitDialogCode">
          <v-card-title class="primary white--text py-2">
            ภาพรวมวงจรงานพนักงานราชการ
            <v-spacer />
            <v-btn icon dark @click="unitDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pt-3">
            <GovemLifecycleHub :key="unitDialogCode" mode="unit" link-base="admin" :college-code="unitDialogCode" />
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
// ศูนย์ติดตามวงจรงานพนักงานราชการ: กรอบอัตรา → ทะเบียน/สัญญาจ้าง → การลา → ประเมิน ปผ.พนร.
// → เลื่อนค่าตอบแทน → ย้าย → ลาออก/พ้นสภาพ  (ข้อมูลจาก govem_lifecycle.php — อ่านอย่างเดียว)
import * as XLSX from "xlsx";

const API = "govem_lifecycle.php";
const CERT = {
  accepted: ["รับรองแล้ว", "success"],
  submitted: ["รอส่วนกลางตรวจ", "info"],
  rejected: ["ส่งกลับแก้ไข", "error"],
  none: ["ยังไม่ส่ง", "warning"]
};

export default {
  name: "GovemLifecycleHub",
  props: {
    mode: { type: String, default: "unit" }, // unit | national
    linkBase: { type: String, default: "college" }, // college | iven | admin — ใช้สร้างลิงก์ไปหน้าดำเนินการ
    collegeCode: { type: String, default: null } // ส่วนกลางดูหน่วยงานที่ระบุ
  },

  data() {
    return {
      loading: false,
      error: "",
      unit: null,
      national: null,
      search: "",
      filter: "action",
      unitDialog: false,
      unitDialogCode: null,
      nationalHeaders: [
        { text: "หน่วยงาน", value: "college_name" },
        { text: "ครอง/กรอบ", value: "frame_filled", align: "center" },
        { text: "สัญญาจ้าง", value: "contract_expired" },
        { text: "ลาออกถึงกำหนด (ยังครองตำแหน่ง)", value: "resign_overdue", align: "center" },
        { text: "ประเมิน ปีก่อน (รายงาน/ต้องรายงาน)", value: "prev_missing", align: "center" },
        { text: "ไม่มีข้อมูลการลา", value: "attendance_missing", align: "center" },
        { text: "แบบรับรองค่าตอบแทน", value: "cert_status", align: "center" },
        { text: "คำขอย้ายรอตอบ", value: "transfer_incoming", align: "center" },
        { text: "", value: "province_name", align: " d-none" },
        { text: "", value: "college_code", align: " d-none" }
      ]
    };
  },

  computed: {
    links() {
      const b = this.linkBase;
      if (b === "admin") {
        return {
          frame: "/admin/gov_em/government_employee",
          record: null,
          assessment: "/admin/gov_em/admin_assessment_report",
          compensation: "/admin/gov_em/central_compensation",
          transfer: null,
          resign: "/admin/gov_em/ResignHistoryPage"
        };
      }
      return {
        frame: `/${b}/gov_em/government_employee`,
        record: `/${b}/gov_em/gov_emid`,
        assessment: `/${b}/gov_em/assessment_govem`,
        compensation: `/${b}/gov_em/cal_compensation`,
        transfer: `/${b}/gov_em/transfer_requests`,
        resign: `/${b}/gov_em/government_employee`
      };
    },
    cards() {
      const u = this.unit;
      if (!u) return [];
      const s = u.signals;
      const f = u.frame;
      const prev = u.assessment[u.prev_fiscal_year];
      const cur = u.assessment[u.fiscal_year];
      const comp = u.compensation;
      const cards = [];

      const regDiff = f.registry - f.frame_filled;
      cards.push({
        key: "frame", title: "กรอบอัตรากำลัง", to: this.links.frame,
        level: regDiff !== 0 ? "warn" : "ok",
        lines: [
          { text: `กรอบ ${f.frame_total} ตำแหน่ง · มีผู้ครอง ${f.frame_filled} · ว่าง ${f.frame_vacant}` },
          { text: `ทะเบียนประวัติ ${f.registry} คน` + (regDiff ? ` (ต่างจากผู้ครองกรอบ ${regDiff > 0 ? "+" : ""}${regDiff})` : ""),
            cls: regDiff ? "warning--text" : "" }
        ]
      });

      const total = f.registry || 0;
      cards.push({
        key: "contract", title: "ทะเบียนประวัติและสัญญาจ้าง", to: this.links.record, action: "ทะเบียนประวัติ",
        level: s.contract_expired ? "error" : s.contract_expiring ? "warn" : s.contract_none ? "info" : "ok",
        lines: [
          { text: `สัญญาสิ้นสุดแล้ว ${s.contract_expired} คน`, cls: s.contract_expired ? "error--text font-weight-bold" : "" },
          { text: `สัญญาจะสิ้นสุดภายใน 180 วัน ${s.contract_expiring} คน`, cls: s.contract_expiring ? "warning--text" : "" },
          { text: s.contract_none ? `ยังไม่บันทึกข้อมูลสัญญาจ้าง ${s.contract_none}/${total} คน` : "บันทึกข้อมูลสัญญาครบ",
            cls: s.contract_none ? "grey--text" : "" }
        ],
        list: u.contract_list.map(p => ({ ...p, detail: (p.contract_state === "expired" ? "หมด " : "สิ้นสุด ") + p.contract_end }))
      });

      cards.push({
        key: "attendance", title: `การลา/มาสาย/ขาดราชการ ปีงบ ${u.prev_fiscal_year}`, to: this.links.assessment,
        level: u.attendance_missing ? "warn" : "ok",
        lines: [
          { text: u.attendance_missing ? `ยังไม่มีข้อมูล ${u.attendance_missing} คน (ใช้ตรวจสิทธิ์เลื่อนค่าตอบแทน ข้อ 1(7)-(9))` : "บันทึกครบทุกคน",
            cls: u.attendance_missing ? "warning--text" : "" }
        ]
      });

      [[u.prev_fiscal_year, prev, false], [u.fiscal_year, cur, true]].forEach(([y, a, isCur]) => {
        if (!a) return;
        const missing = a.r1.missing + a.r2.missing;
        const unapproved = a.r1.draft + a.r1.rejected + a.r1.submitted + a.r2.draft + a.r2.rejected + a.r2.submitted;
        const notDue = a.r1.not_due + a.r2.not_due;
        cards.push({
          key: "assess" + y, title: `ประเมินผล ปผ.พนร. ปีงบ ${y}` + (isCur ? " (ปีปัจจุบัน)" : ""), to: this.links.assessment,
          level: missing ? "error" : unapproved ? "warn" : notDue ? "info" : "ok",
          lines: [
            { text: `รอบ 1: รายงาน ${a.r1.assessed}/${a.r1.required}` + (a.r1.not_due ? " (ยังไม่สิ้นรอบ)" : "") +
              ` · รอบ 2: รายงาน ${a.r2.assessed}/${a.r2.required}` + (a.r2.not_due ? " (ยังไม่สิ้นรอบ)" : "") },
            { text: missing ? `ยังไม่รายงาน ${missing} รายการ` : notDue ? "อยู่ระหว่างรอบ" : "รายงานครบ",
              cls: missing ? "error--text font-weight-bold" : "" },
            { text: unapproved ? `ยังไม่อนุมัติ/ร่าง/ส่งกลับ ${unapproved} รายการ` : "", cls: "warning--text" },
            { text: a.issues ? `ข้อมูลบุคลากรที่ต้องตรวจสอบ ${a.issues} คน` : "", cls: "grey--text" }
          ].filter(l => l.text)
        });
      });

      if (comp) {
        const c = comp.unit;
        const st = c ? c.cert_status || "none" : null;
        cards.push({
          key: "comp", title: `เลื่อนค่าตอบแทน (ผลประเมินปีงบ ${comp.run.comp_year})`, to: this.links.compensation,
          level: !c ? "info" : st === "accepted" ? "ok" : st === "submitted" ? "info" : st === "rejected" ? "error" : "warn",
          lines: c
            ? [{ text: `ส่วนกลางยืนยันผลแล้ว · มีสิทธิ์ ${c.eligible_count}/${c.personnel_count} คน · เลื่อนรวม ${this.fmt(c.distributed_month)} บาท/เดือน` },
               { text: `แบบรับรอง ผอ.: ${this.certText(st)}`, cls: st === "accepted" ? "success--text" : "warning--text" }]
            : [{ text: "ไม่มีผลของหน่วยงานในรอบที่ยืนยันล่าสุด" }]
        });
      } else {
        cards.push({ key: "comp", title: "เลื่อนค่าตอบแทน", to: this.links.compensation, level: "info",
          lines: [{ text: "ส่วนกลางยังไม่ยืนยันผลการประมวลผล" }] });
      }

      const t = u.transfer;
      cards.push({
        key: "transfer", title: "การย้ายระหว่างหน่วยงาน", to: this.links.transfer,
        level: t.incoming ? "warn" : "ok",
        lines: [
          { text: `คำขอที่รอหน่วยงานนี้ตอบรับ ${t.incoming} รายการ`, cls: t.incoming ? "warning--text font-weight-bold" : "" },
          { text: `คำขอที่ส่งไปรอหน่วยงานอื่นตอบ ${t.outgoing} รายการ` }
        ]
      });

      cards.push({
        key: "resign", title: "ลาออก/พ้นสภาพ", to: this.links.resign, action: "ดำเนินการพ้นสภาพ",
        level: s.resign_overdue ? "error" : s.resign_upcoming ? "info" : "ok",
        lines: [
          { text: `ถึงวันลาออกแล้วแต่ยังครองตำแหน่ง ${s.resign_overdue} คน`, cls: s.resign_overdue ? "error--text font-weight-bold" : "" },
          { text: s.resign_overdue ? "ต้องดำเนินการพ้นสภาพ (คืนตำแหน่งว่าง) ที่หน้ากรอบอัตรากำลัง" : "", cls: "error--text" },
          { text: `บันทึกลาออกแล้ว รอวันมีผล ${s.resign_upcoming} คน` }
        ].filter(l => l.text),
        list: u.resign_list.map(p => ({ ...p, detail: (p.resign_state === "overdue" ? "มีผลแล้ว " : "มีผล ") + p.resignation_date }))
      });
      return cards;
    },
    overall() {
      const levels = this.cards.map(c => c.level);
      if (levels.includes("error")) return { text: "มีเรื่องต้องดำเนินการเร่งด่วน", color: "error", icon: "mdi-alert-octagon" };
      if (levels.includes("warn")) return { text: "มีเรื่องที่ต้องตรวจสอบ", color: "warning", icon: "mdi-alert" };
      return { text: "เรียบร้อย", color: "success", icon: "mdi-check-circle" };
    },
    units() {
      return (this.national && this.national.units) || [];
    },
    actionUnits() {
      return this.units.filter(this.needsAction);
    },
    filteredUnits() {
      if (this.filter === "action") return this.actionUnits;
      if (this.filter === "ok") return this.units.filter(u => !this.needsAction(u));
      return this.units;
    },
    nationalKpis() {
      const s = k => this.units.reduce((a, u) => a + (+u[k] || 0), 0);
      return [
        { label: "หน่วยงาน", value: this.units.length, hint: `ต้องดำเนินการ ${this.actionUnits.length}` },
        { label: "ผู้ครองกรอบ / ว่าง", value: s("frame_filled"), hint: `ว่าง ${this.fmt(s("frame_vacant"))} ตำแหน่ง` },
        { label: "สัญญาสิ้นสุดแล้ว", value: s("contract_expired"), hint: `ใกล้สิ้นสุด ${this.fmt(s("contract_expiring"))} · ไม่มีข้อมูล ${this.fmt(s("contract_none"))}`, bad: true, color: "error--text", bg: "red lighten-5" },
        { label: "ลาออกถึงกำหนด ยังครองตำแหน่ง", value: s("resign_overdue"), hint: `รอมีผล ${this.fmt(s("resign_upcoming"))}`, bad: true, color: "error--text", bg: "red lighten-5" },
        { label: `ยังไม่รายงานประเมิน ปีงบ ${this.national.prev_fiscal_year}`, value: s("prev_missing"), hint: `จาก ${this.fmt(s("prev_required"))} รายการ`, bad: true, color: "error--text", bg: "red lighten-5" },
        { label: "ไม่มีข้อมูลการลา", value: s("attendance_missing"), hint: `ปีงบ ${this.national.prev_fiscal_year}`, bad: true, color: "warning--text", bg: "orange lighten-5" }
      ];
    }
  },

  watch: {
    collegeCode() {
      this.load();
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    fmt(v) {
      return Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 0 });
    },
    levelColor(l) {
      return { ok: "success", info: "info", warn: "warning", error: "error" }[l] || "grey";
    },
    levelIcon(l) {
      return { ok: "mdi-check-circle", info: "mdi-information", warn: "mdi-alert", error: "mdi-alert-octagon" }[l];
    },
    certText(s) {
      return s ? (CERT[s] || [s])[0] : "-";
    },
    certColor(s) {
      return s ? (CERT[s] || [s, "grey"])[1] : "grey lighten-1";
    },
    needsAction(u) {
      return u.contract_expired || u.resign_overdue || u.prev_missing || u.prev_unapproved ||
        u.transfer_incoming || u.cert_status === "none" || u.cert_status === "rejected";
    },
    openUnit(u) {
      this.unitDialogCode = u.college_code;
      this.unitDialog = true;
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const payload = { ApiKey: "HRvec2021", action: this.mode === "national" ? "national" : "unit" };
        if (this.collegeCode) payload.college_code = this.collegeCode;
        const res = await this.$http.post(API, payload);
        if (!res.data.status) throw new Error(res.data.message);
        if (this.mode === "national") this.national = res.data;
        else this.unit = res.data;
      } catch (e) {
        this.error = "โหลดข้อมูลไม่สำเร็จ: " + ((e.response && e.response.data && e.response.data.message) || e.message);
      } finally {
        this.loading = false;
      }
    },
    exportXlsx() {
      const rows = this.filteredUnits.map((u, i) => ({
        ลำดับ: i + 1, รหัส: u.college_code, หน่วยงาน: u.college_name, จังหวัด: u.province_name, ภาค: u.region_name,
        กรอบอัตรา: u.frame_total, ผู้ครอง: u.frame_filled, ว่าง: u.frame_vacant, ทะเบียนประวัติ: u.registry,
        สัญญาสิ้นสุดแล้ว: u.contract_expired, สัญญาใกล้สิ้นสุด: u.contract_expiring, ไม่มีข้อมูลสัญญา: u.contract_none,
        ลาออกถึงกำหนดยังครองตำแหน่ง: u.resign_overdue, ลาออกรอมีผล: u.resign_upcoming,
        [`ประเมินปีงบ ${this.national.prev_fiscal_year} ต้องรายงาน`]: u.prev_required,
        [`ประเมินปีงบ ${this.national.prev_fiscal_year} ยังไม่รายงาน`]: u.prev_missing,
        ยังไม่อนุมัติ: u.prev_unapproved, ข้อมูลบุคลากรต้องตรวจ: u.data_issues, ไม่มีข้อมูลการลา: u.attendance_missing,
        แบบรับรองค่าตอบแทน: this.certText(u.cert_status), คำขอย้ายรอตอบ: u.transfer_incoming
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "ภาพรวมพนักงานราชการ");
      XLSX.writeFile(wb, `ภาพรวมวงจรงานพนักงานราชการ_${this.national.fiscal_year}.xlsx`);
    }
  }
};
</script>

<style scoped>
.step-card {
  height: 100%;
  border-left-width: 4px !important;
}
.step-ok {
  border-left-color: #43a047 !important;
}
.step-info {
  border-left-color: #1e88e5 !important;
}
.step-warn {
  border-left-color: #fb8c00 !important;
}
.step-error {
  border-left-color: #e53935 !important;
}
.hub-table >>> td {
  vertical-align: top;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
</style>
